import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  DOCUMENT,
  ElementRef,
  inject,
} from '@angular/core';
import {
  faUserCircle,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import {
  faFacebook,
  faXTwitter,
  faInstagram,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';

import { SharedUserService } from '../shared/services/shared/user/shared-user.service';
import { Meta, Title } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { ParticlesConfig } from '../shared/configs/particles';
import { HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { loadComponentTranslations } from '../shared/funcs/lang-loader';
import { Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { NavbarControlComponent } from '../shared/controls/navbar-control/navbar-control.component';
import { HowItWorkComponent } from '../shared/controls/how-it-work/how-it-work.component';
import { TestimonialsComponent } from '../shared/controls/testimonials/testimonials.component';
import { WhoCanBenifitComponent } from '../shared/controls/who-can-benifit/who-can-benifit.component';
import { FeaturesComponent } from '../shared/controls/features/features.component';
import { FormsModule } from '@angular/forms';
import { GetInTouchWithUsComponent } from '../shared/forms/get-in-touch-with-us/get-in-touch-with-us.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { HeroSectionComponent } from '../shared/controls/hero-section/hero-section.component';
import { MeetClockflexComponent } from '../shared/controls/meet-clockflex/meet-clockflex.component';
import { ContactSectionComponent } from '../shared/controls/contact-section/contact-section.component';
import { FooterComponent } from '../shared/controls/footer/footer.component';
import { Subscription, timer } from 'rxjs';
// declare let particlesJS: any; // Required to be properly interpreted by TypeScript.

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  standalone: true,
  imports: [
    NavbarControlComponent,
    HowItWorkComponent,
    TestimonialsComponent,
    WhoCanBenifitComponent,
    FeaturesComponent,
    TranslateModule,
    RouterModule,
    CommonModule,
    FormsModule,
    GetInTouchWithUsComponent,
    HeroSectionComponent,
    MeetClockflexComponent,
    ContactSectionComponent,
    FooterComponent,
  ],
})
export class HomeComponent implements OnInit {
  @ViewChild('getInTouchWithUSModel') public getInTouchWithUSModel: ElementRef =
    null!;

  public ngModal: NgbModal = inject(NgbModal);
  public ngModalRef: NgbModalRef = null!;

  public invokeParticles(): void {
    if (!this.isBrowser()) return;
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  public faUserCircle: IconDefinition = faUserCircle;
  public faInstagram = faInstagram;
  public faXTwitter = faXTwitter;
  public faLinkedin = faLinkedin;
  public faFacebook = faFacebook;

  public isLoggedIn: boolean = false;
  public selectedLanguage: string = 'en';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private _sharedUserService: SharedUserService,
    private _router: Router,
    private titleService: Title,
    private _changeDetectRef: ChangeDetectorRef,
    private _http: HttpClient,
    private _translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document,
    private metaService: Meta,
  ) {}

  public ngOnInit(): void {
    // ✅ Language (SSR-safe default)
    this.selectedLanguage = 'en';

    if (this.isBrowser()) {
      this.selectedLanguage = localStorage.getItem('language') || 'en';
    }
    loadComponentTranslations(
      this._http,
      this._translateService,
      'components/home',
      this.isBrowser(),
    ).subscribe({
      next: () => {},
      error: () => {},
    });

    this.subscribeUserService();
    this.invokeParticles();

    const homepageUrl = 'https://clockflex.com/';

    // Title
    this.titleService.setTitle('ClockFlex | Booking & Scheduling Software');

    // Canonical URL
    this.setCanonicalUrl(homepageUrl);

    // Meta description
    this.metaService.updateTag({
      name: 'description',
      content:
        'Book and manage appointments, facilities, resources, and services with ClockFlex. Simplify scheduling, coordinate teams, and let customers book online.',
    });

    // Open Graph
    this.metaService.updateTag({
      property: 'og:title',
      content: 'ClockFlex | Booking & Scheduling Software',
    });

    this.metaService.updateTag({
      property: 'og:description',
      content:
        'One platform for booking services, reserving resources, and managing schedules. Help customers book online with ClockFlex.',
    });

    this.metaService.updateTag({
      property: 'og:url',
      content: homepageUrl,
    });

    this.metaService.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.metaService.updateTag({
      property: 'og:image',
      content: 'https://clockflex.com/assets/images/og-image.png',
    });

    // Twitter / X
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.metaService.updateTag({
      name: 'twitter:title',
      content: 'ClockFlex | Booking & Scheduling Software',
    });

    this.metaService.updateTag({
      name: 'twitter:description',
      content:
        'Simplify bookings for services, facilities, and resources with ClockFlex.',
    });

    this.metaService.updateTag({
      name: 'twitter:image',
      content: 'https://clockflex.com/assets/images/og-image.png',
    });

    this.startPopupTimer();
  }

  private popupTimer: any;

  ngOnDestroy(): void {
    this.popupSubscription?.unsubscribe();
  }
  private popupSubscription?: Subscription;

  private startPopupTimer(): void {
    this.popupSubscription = timer(30000).subscribe(() => {
      this.openGetInTouchWithUsModal();
    });
  }
  public openGetInTouchWithUsModal(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Don't show popup to logged-in users
    if (this.isLoggedIn) {
      return;
    }

    const lastOpened = localStorage.getItem('lastOpenedPopupDT');

    const shouldOpen =
      !lastOpened ||
      !moment(lastOpened).isValid() ||
      moment().diff(moment(lastOpened), 'days') >= 30;

    if (shouldOpen) {
      this.openModal();
      localStorage.setItem('lastOpenedPopupDT', moment().toISOString());
    }
  }

  private setCanonicalUrl(url: string): void {
    if (!url.startsWith('http')) {
      return; // safety guard
    }

    let link: HTMLLinkElement | null = this.document.querySelector(
      "link[rel='canonical']",
    );

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  public changeLanguage(e: any): void {
    if (!this.isBrowser()) return;

    localStorage.setItem('language', this.selectedLanguage);
    this._translateService.use(this.selectedLanguage);
    window.location.reload();
  }

  private subscribeUserService(): void {
    this._sharedUserService.loggedIn$.subscribe((response) => {
      this.isLoggedIn = response ? true : false;
      if (this.isBrowser()) {
        this._changeDetectRef.detectChanges();
      }
    });
  }

  openModal() {
    this.ngModalRef = this.ngModal.open(this.getInTouchWithUSModel, {});
  }

  closeModal(e: string) {
    this.ngModalRef?.close();
  }
}
